import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Save, X, PlayCircle, Youtube, Instagram, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const EMPTY = { url: "", title: "", enabled: true };

export default function AdminShortsFeedTab() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const [autoScroll, setAutoScroll] = useState(8);

  const reload = async () => {
    const r = await api.get("/admin/shorts-feed");
    setItems(r.data || []);
    try {
      const s = await api.get("/settings/public");
      setAutoScroll(Number(s.data?.shorts_autoscroll_seconds) || 8);
    } catch (_) { /* noop */ }
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      if (draft.id) await api.put(`/admin/shorts-feed/${draft.id}`, draft);
      else await api.post("/admin/shorts-feed", draft);
      toast.success("Guardado");
      setDraft(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar este video del feed?")) return;
    await api.delete(`/admin/shorts-feed/${id}`);
    toast.success("Eliminado");
    reload();
  };
  const toggleEnabled = async (it) => {
    await api.put(`/admin/shorts-feed/${it.id}`, { ...it, enabled: !it.enabled });
    reload();
  };
  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    await api.post("/admin/shorts-feed/reorder", { order: next.map((c) => c.id) });
  };
  const saveAutoScroll = async () => {
    try {
      await api.post("/admin/settings", { key: "shorts_autoscroll_seconds", value: Number(autoScroll) || 8 });
      toast.success("Velocidad de auto-scroll guardada");
    } catch (e) { toast.error("Error"); }
  };

  const icon = (p) => p === "youtube" ? <Youtube size={14} className="text-red-500" /> : p === "instagram" ? <Instagram size={14} className="text-pink-500" /> : <PlayCircle size={14} className="text-sky-500" />;

  return (
    <div data-testid="admin-shorts-feed-tab">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-headline uppercase text-3xl md:text-4xl flex items-center gap-2"><PlayCircle size={26} /> Feed de Shorts</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>YouTube Shorts, Instagram Reels y TikTok. Scroll infinito vertical 9:16.</p>
        </div>
        <button onClick={() => setDraft({ ...EMPTY })} className="btn-accent" data-testid="admin-short-new">
          <Plus size={16} /> Nuevo
        </button>
      </div>

      {/* Autoscroll setting */}
      <div className="border rounded-lg p-3 mb-5 flex items-center gap-3 flex-wrap" style={{ borderColor: "var(--border)" }}>
        <label className="text-xs font-bold uppercase tracking-widest">Auto-scroll (segundos)</label>
        <input type="number" min="0" max="60" value={autoScroll} onChange={(e) => setAutoScroll(e.target.value)} className="w-20 px-2 py-1 border rounded text-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-short-autoscroll" />
        <span className="text-[10px] opacity-70">0 = desactivado</span>
        <button onClick={saveAutoScroll} className="text-xs px-3 py-1.5 rounded border font-bold uppercase" style={{ borderColor: "var(--border)" }} data-testid="admin-short-save-autoscroll">Guardar</button>
      </div>

      {items.length === 0 ? (
        <p className="text-center py-12 text-sm" style={{ color: "var(--text-muted)" }}>Sin videos. Agregá el primero.</p>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={it.id} className="border rounded-lg p-3 flex items-center gap-3 flex-wrap" style={{ borderColor: "var(--border)", opacity: it.enabled ? 1 : 0.5 }} data-testid={`admin-short-row-${it.id}`}>
              <div className="flex flex-col text-[10px] opacity-70">
                <button onClick={() => move(idx, -1)} className="hover:opacity-100 px-1">▲</button>
                <button onClick={() => move(idx, 1)} className="hover:opacity-100 px-1">▼</button>
              </div>
              {it.thumbnail && (
                <img src={it.thumbnail} alt="" className="w-14 h-20 object-cover rounded flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {icon(it.provider)}
                  <span className="text-[10px] font-bold uppercase opacity-70">{it.provider}</span>
                  {!it.enabled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 font-bold">OCULTO</span>}
                </div>
                <p className="font-bold text-sm truncate mt-1">{it.title || it.video_id}</p>
                <a href={it.url} target="_blank" rel="noreferrer" className="text-xs opacity-70 flex items-center gap-1 truncate hover:opacity-100"><ExternalLink size={10} /> {it.url}</a>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => toggleEnabled(it)} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }}>{it.enabled ? "Ocultar" : "Mostrar"}</button>
                <button onClick={() => setDraft(it)} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }} data-testid={`admin-short-edit-${it.id}`}><Edit size={12} /></button>
                <button onClick={() => del(it.id)} className="text-xs px-2 py-1.5 rounded border font-bold" style={{ borderColor: "var(--border)" }} data-testid={`admin-short-delete-${it.id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDraft(null)}>
          <div className="bg-[var(--bg)] max-w-lg w-full rounded-lg p-5 space-y-3 border" style={{ borderColor: "var(--border)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-headline uppercase text-xl">{draft.id ? "Editar" : "Nuevo"} short</h3>
              <button onClick={() => setDraft(null)}><X size={18} /></button>
            </div>
            <input type="url" placeholder="URL (YouTube Shorts, TikTok, Instagram Reel)" value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="admin-short-url" />
            <input type="text" placeholder="Título (opcional)" value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="w-full px-3 py-2 border rounded bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.enabled !== false} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} /> Visible en el feed
            </label>
            <button onClick={save} className="btn-accent w-full" data-testid="admin-short-save"><Save size={14} /> Guardar</button>
          </div>
        </div>
      )}
    </div>
  );
}
